import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, Tag, ChevronLeft, ChevronRight } from 'lucide-react'
import { blog } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

function parseInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i}>{part.slice(1, -1)}</em>
    return part
  })
}

function MarkdownBody({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/)
  return (
    <div className="prose-blog">
      {blocks.map((block, i) => {
        const trimmed = block.trim()
        if (!trimmed) return null
        if (trimmed.startsWith('## '))
          return <h2 key={i}>{trimmed.slice(3)}</h2>
        if (trimmed.startsWith('# '))
          return <h2 key={i}>{trimmed.slice(2)}</h2>
        if (trimmed.startsWith('### '))
          return <h3 key={i}>{trimmed.slice(4)}</h3>
        if (trimmed === '---' || trimmed === '***')
          return <hr key={i} />
        // List detection — handle inline newlines within block
        const lines = trimmed.split('\n')
        if (lines.every(l => l.match(/^[\-–•]\s/))) {
          return (
            <ul key={i}>
              {lines.map((l, j) => <li key={j}>{parseInline(l.replace(/^[\-–•]\s+/, ''))}</li>)}
            </ul>
          )
        }
        if (lines.every(l => l.match(/^\d+\.\s/))) {
          return (
            <ol key={i}>
              {lines.map((l, j) => <li key={j}>{parseInline(l.replace(/^\d+\.\s+/, ''))}</li>)}
            </ol>
          )
        }
        // Blockquote-like: starts with " or —
        if (trimmed.startsWith('"') || (trimmed.startsWith('—') && trimmed.length < 120))
          return <blockquote key={i}>{parseInline(trimmed)}</blockquote>
        return <p key={i}>{parseInline(trimmed)}</p>
      })}
    </div>
  )
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const postIndex = blog.posts.findIndex(p => p.slug === slug)
  const post = blog.posts[postIndex]
  const prev = blog.posts[postIndex + 1]
  const next = blog.posts[postIndex - 1]

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Post not found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline flex items-center gap-2 justify-center">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">
      {/* Hero image */}
      {post.image && (
        <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
        </div>
      )}

      {/* Header */}
      <div className={`${post.image ? 'bg-slate-900 -mt-1' : 'bg-slate-900 pt-28'} pb-12`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          <div className="flex items-center gap-4 mb-4 text-sm text-slate-400">
            {post.category && (
              <span className="flex items-center gap-1.5 text-blue-400 font-semibold uppercase tracking-widest text-xs">
                <Tag size={12} /> {post.category}
              </span>
            )}
            {post.date && (
              <span className="flex items-center gap-1.5">
                <Calendar size={12} />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {post.body ? (
            <MarkdownBody text={post.body} />
          ) : (
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed italic">
              {post.excerpt}
            </p>
          )}
        </motion.div>

        {/* CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-slate-900 rounded-3xl p-8 text-center"
        >
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Work with Douglas</p>
          <h3 className="text-2xl font-bold text-white mb-3">Ready to explore your inner world?</h3>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Start with a free consultation. No pressure — just a real conversation about where you are and where you want to be.
          </p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent">
            Book Free Consultation
          </CalendlyBtn>
        </motion.div>

        {/* Prev / Next */}
        {(prev || next) && (
          <div className="mt-12 grid grid-cols-2 gap-4">
            {prev ? (
              <Link
                to={`/blog/${prev.slug}`}
                className="flex items-start gap-3 p-5 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
              >
                <ChevronLeft size={18} className="mt-0.5 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
                <div>
                  <div className="text-xs text-slate-400 mb-1">Previous</div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 leading-snug line-clamp-2">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : <div />}
            {next ? (
              <Link
                to={`/blog/${next.slug}`}
                className="flex items-start gap-3 p-5 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group text-right justify-end"
              >
                <div>
                  <div className="text-xs text-slate-400 mb-1">Next</div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 leading-snug line-clamp-2">
                    {next.title}
                  </div>
                </div>
                <ChevronRight size={18} className="mt-0.5 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
              </Link>
            ) : <div />}
          </div>
        )}
      </div>
    </div>
  )
}
